import { chunk } from 'lodash';

// Phases the browser drives itself once the server has finished: saving the
// prepared artifact to disk. Nothing on the server can update these.
const BROWSER_PHASES = new Set(['ready_to_save', 'downloading', 'complete']);

let lastDownloadAttempt = 0;

/**
 * A new ``downloadAttempt`` for a download card created for a new request. A retry reuses the download
 * id, so this is what tells its card from the earlier request's.
 */
export function newDownloadAttempt() {
    lastDownloadAttempt += 1;
    return lastDownloadAttempt;
}

/**
 * The attempt of each download card still waiting on the server (building or pushing), by download id:
 * the downloads whose final ``downloadCompletionUpdate`` a WebSocket gap can lose.
 */
export function serverPendingDownloadAttempts(progressStatus) {
    return new Map(
        Object.entries(progressStatus || {})
            .filter(
                ([, entry]) =>
                    entry?.isDownload && !entry.isCompleted && !entry.isError && !BROWSER_PHASES.has(entry.phase)
            )
            .map(([key, entry]) => [entry.downloadId || key, entry.downloadAttempt])
    );
}

// ``MAX_DOWNLOAD_IDS`` in backend/app/routes/download_delivery_route.py: more ids fail the whole request.
const MAX_DOWNLOAD_IDS_PER_REQUEST = 50;

/** GET paths for the stored delivery states of *downloadIds* requested from this browser, within the endpoint's id limit. */
export function downloadDeliveryStatesPaths(clientUuid, downloadIds) {
    return chunk(downloadIds, MAX_DOWNLOAD_IDS_PER_REQUEST).map((ids) => {
        const params = new URLSearchParams({ client_uuid: clientUuid });
        ids.forEach((id) => params.append('download_id', id));
        return `download-delivery-states?${params}`;
    });
}

/**
 * The stored *states* worth replaying: those of downloads still waiting on the server now, in the attempt
 * that was pending when the request started (*attemptsAtRequest*, from `serverPendingDownloadAttempts`).
 * One that finished live, was dismissed or was retried while the request was pending is newer than its
 * stored state.
 */
export function statesOfServerPendingDownloads(states, progressStatus, attemptsAtRequest) {
    const pending = serverPendingDownloadAttempts(progressStatus);
    return (states || []).filter((state) => {
        const id = state?.downloadId;
        return pending.has(id) && attemptsAtRequest.has(id) && pending.get(id) === attemptsAtRequest.get(id);
    });
}
